import React from 'react';
import PropTypes from 'prop-types';
import { withNamespaces } from 'react-i18next';
import { useObserver } from 'mobx-react';

import { routeNode } from 'core/routing';

import DocumentHead from 'legacyComponents/documentHead';

import { useRouterStore, useAppManager } from 'apps/hooks';
import { FourOhFour } from 'apps/fourOhFour/index';
import Header from 'apps/components/header/header';
import { headerStore } from 'apps/components/header/app';
import { clientStore } from 'apps/client/app';
import { ForumRouter } from 'apps/forum/router';
import { InitiativeProjectRouter } from 'apps/project/router';
import { InitiativeSharedFileRouter } from 'apps/share/router';
import { InitiativeStoryRouter } from 'apps/story/main/router';
import { CollectionPage } from 'apps/collection/main/index';
import { imageSrc } from 'apps/file/utils';
import { conf as globalConf } from 'apps/config.yaml';
import { ThreadBody } from 'apps/forum/thread/body';
import { SideView } from 'apps/forum/views/side';

import { InitiativeOverviewTab } from 'apps/initiative/tabs/overview/index';
import { InitiativeProjectsTab } from 'apps/initiative/tabs/projects/index';
import { InitiativeStoriesTab } from 'apps/initiative/tabs/stories/index';
// import { InitiativeCollectionsTab } from "apps/initiative/tabs/collections/index";
import { InitiativeAppManager } from 'apps/initiative/appManager';
import { InitiativeIntro } from 'apps/initiative/views/intro';
import { PlanMonitor } from 'apps/initiative/views/PlanMonitor';

// import { InitiativeSettingsRouter } from './settings/router';
// import { ServicesRouter } from 'apps/initiative/tabs/services/router';

const viewsMap = {
  '': InitiativeOverviewTab,
  projects: InitiativeProjectsTab,
  stories: InitiativeStoriesTab,
  // collections: InitiativeCollectionsTab,
  forum: ForumRouter,
  project: InitiativeProjectRouter,
  shared: InitiativeSharedFileRouter,
  story: InitiativeStoryRouter,
  collection: CollectionPage,
  // settings: InitiativeSettingsRouter,
  // services: ServicesRouter,
};

function requireInitiativeManager(route) {
  if (['project', 'story'].includes(route.name.split('.')[1])) {
    return false;
  }
  return true;
}

const InitiativeSpace = props => {
  const { route, params } = useRouterStore();
  const requireIM = requireInitiativeManager(route);
  const appManager = useAppManager(
    InitiativeAppManager,
    null,
    props,
    requireIM,
  );

  return useObserver(() => {
    if (appManager?.queries.get('initiative')?.notFound) {
      return <FourOhFour type='Organisation' />;
    }

    const initiative = appManager?.initiative;
    const loading = appManager?.loading;
    if (initiative === null || loading) {
      // TODO: loading states
      return <Header clientStore={clientStore} headerStore={headerStore} />;
    }

    const { componentFromRoute } = props;
    const introVisible = [
      'initiative',
      'initiative.projects',
      'initiative.stories',
      // "initiative.collections",
      'initiative.forum',
      'initiative.forum.category',
      'initiative.forum.thread',
      'initiative.services',
    ];
    const View = componentFromRoute();
    const thread = params.get('thread');

    let header = <Header />;
    // happens to be the case that it the same places we don't want the initiative manager
    // We also don't want to insert the header at this level
    if (!requireIM) {
      header = null;
    }

    return (
      <PlanMonitor>
        {initiative && (
          <DocumentHead
            title={`${initiative.title}`}
            description={initiative.description}
            imagePath={imageSrc(
              initiative.avatar,
              '',
              globalConf.imgSizes.initiative.large,
            )}
          />
        )}
        {header}
        {introVisible.includes(route.name) && (
          <InitiativeIntro initiativeManager={appManager} route={route} />
        )}
        <View
          initiativeManager={appManager}
          params={params}
          routeName={route.name}
        />
        {!!thread && (
          <SideView>
            <ThreadBody params={params} preview />
          </SideView>
        )}
      </PlanMonitor>
    );
  });
};

const InitiativeSpaceI18n = withNamespaces()(InitiativeSpace);

InitiativeSpace.propTypes = {
  componentFromRoute: PropTypes.func,
};

export const InitiativeRouter = routeNode(
  'initiative',
  viewsMap,
  FourOhFour,
)(InitiativeSpaceI18n);
